import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { jwtDecode } from 'jwt-decode';
import API_URL from '../config';
import { loadStripe } from '@stripe/stripe-js';
import './Bookings.css';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [roomNames, setRoomNames] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmails, setUserEmails] = useState({});
  const navigate = useNavigate();

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token || typeof token !== 'string') {
        console.warn('Invalid or missing token, redirecting to login.');
        setMessage('Session expired, please log in again.');
        navigate('/login');
        return;
      }

      const decodedToken = jwtDecode(token);
      const userId = decodedToken.id;
      const isAdminFlag = decodedToken.role === 'admin';
      setIsAdmin(isAdminFlag);

      const bookingsEndpoint = isAdminFlag
        ? `${API_URL}/api/bookings`
        : `${API_URL}/api/bookings/user/${userId}`;

      const response = await api.get(bookingsEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fetchedBookings = response.data;
      const activeBookings = fetchedBookings
        .filter(booking => booking.status !== 'canceled')
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

      const roomIds = [...new Set(activeBookings.map(booking => booking.roomId))];
      const roomDetails = await Promise.all(roomIds.map(roomId =>
        api.get(`${API_URL}/api/rooms/${roomId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ));

      const roomNamesMap = roomDetails.reduce((acc, roomResponse) => {
        const room = roomResponse.data;
        acc[room._id] = room.name;
        return acc;
      }, {});

      setRoomNames(roomNamesMap);

      if (isAdminFlag) {
        const userIds = [...new Set(activeBookings.map(booking => booking.userId))];
        const userDetails = await Promise.all(userIds.map(userId =>
          api.get(`${API_URL}/api/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ));

        const userEmailsMap = userDetails.reduce((acc, userResponse) => {
          const user = userResponse.data;
          acc[user._id] = { name: user.name, email: user.email };
          return acc;
        }, {});

        setUserEmails(userEmailsMap);
      }

      setBookings(activeBookings);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setMessage('Error fetching bookings. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [navigate]);

  const formatDateTime = (startTime, endTime) => {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    return `${startDate.toLocaleDateString('fr-FR', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    })}, de ${startDate.getHours()}h${startDate.getMinutes() === 0 ? '' : startDate.getMinutes()} à ${endDate.getHours()}h${endDate.getMinutes() === 0 ? '' : endDate.getMinutes()}`;
  };

  const handleRedirectToCalendar = () => {
    navigate('/booking-calendar');
  };

  const calculateAmount = (booking) => {
    const roomName = roomNames[booking.roomId];
    const duration = (new Date(booking.endTime) - new Date(booking.startTime)) / (1000 * 60 * 60); // in hours
    let hourlyRate = 0;

    if (roomName === 'Grande') {
      hourlyRate = booking.soloOrGroup === 'Group'
        ? parseFloat(process.env.REACT_APP_PRICE_GRANDE_GROUPE)
        : parseFloat(process.env.REACT_APP_PRICE_GRANDE);
    } else if (roomName === 'Petite') {
      hourlyRate = parseFloat(process.env.REACT_APP_PRICE_PETITE);
    }

    return duration * hourlyRate;
  };

  const getRoomDisplayName = (name, soloOrGroup) => {
    if (name === 'Grande') {
      return `Grande salle (${soloOrGroup === 'Group' ? 'en groupe' : 'en solo'})`;
    } else if (name === 'Petite') {
      return 'Petite salle';
    } else {
      return name;
    }
  };

  const handleCancel = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await api.patch(`${API_URL}/api/bookings/${bookingId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage('Réservation annulée avec succès.');
      setTimeout(() => {
        fetchBookings(); // Refresh the list after 3 seconds
      }, 3000);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setMessage('Error cancelling booking. Please try again.');
    }
	 window.location.reload();
  };

  const handleConfirm = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await api.patch(`${API_URL}/api/bookings/${bookingId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage('Réservation confirmée avec succès.');
      fetchBookings(); // Refresh the list
    } catch (error) {
      console.error('Error confirming booking:', error);
      setMessage('Error confirming booking. Please try again.');
    }
	 window.location.reload();
  };

  const handlePayment = async (booking) => {
    try {
      const stripe = await stripePromise;
      const token = localStorage.getItem('token');
      const amount = calculateAmount(booking) * 100; // Convert to cents
      const productData = getRoomDisplayName(roomNames[booking.roomId], booking.soloOrGroup);

      const response = await api.post(`${API_URL}/api/payments`, {
        amount,
        currency: 'eur',
        bookingId: booking._id,
        productData,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { id } = response.data;
      await stripe.redirectToCheckout({ sessionId: id });
    } catch (error) {
      console.error('Error processing payment:', error);
      setMessage('Erreur lors du paiement.');
    }
  };

  return (
    <div>
      <h2>Vos Réservations</h2>
      {loading ? (
        <p>Veuillez patienter...</p>
      ) : message ? (
        <p>{message}</p>
      ) : (
        <>
          <h3>Mes Réservations à venir</h3>
          <ul>
            {bookings.filter(booking => new Date(booking.endTime) > new Date()).length > 0 ? (
              bookings
                .filter(booking => new Date(booking.endTime) > new Date()) // Filter future bookings
                .map((booking) => (
                  <li key={booking._id}>
                    <p><strong>Sélection :</strong> {roomNames[booking.roomId] === 'Grande' ? 
                    `Grande salle (${booking.soloOrGroup === 'Group' ? 'en groupe' : 'en solo'})` : 'Petite salle'}</p>
                    <p><strong>Date :</strong> {formatDateTime(booking.startTime, booking.endTime)}</p>
                    
                    {isAdmin && userEmails[booking.userId] && (
                      <p><strong>Utilisateur :</strong> {userEmails[booking.userId].name} ({userEmails[booking.userId].email})</p>
                    )}

                    <p><strong>Status :</strong> {booking.status === 'pending' ? 'en cours' : 'confirmé'}</p>
                    
                    <p><strong>Code :</strong> {booking.reservationNumber}</p>
                    
                    {booking.status === 'pending' && (
                      isAdmin ? (
                        <button onClick={() => handleConfirm(booking._id)} style={{ color: 'green' }}>
                          Confirmer
                        </button>
                      ) : (
                        <button onClick={() => handlePayment(booking)} style={{ color: 'blue' }}>
                          Paiement
                        </button>
                      )
                    )}

                    {isAdmin && (
                      <button onClick={() => handleCancel(booking._id)} style={{ marginLeft: '10px', color: 'red' }}>
                        Annuler
                      </button>
                    )}
                  </li>
                ))
            ) : (
              <p>Aucune réservation à venir.</p>
            )}
          </ul>

          <h3>Mes Réservations passées</h3>
          <ul>
            {bookings.filter(booking => new Date(booking.endTime) <= new Date()).length > 0 ? (
              bookings
                .filter(booking => new Date(booking.endTime) <= new Date()) // Filter past bookings
                .map((booking) => (
                  <li key={booking._id} className="past-booking">
                    <p><strong>Sélection :</strong> {roomNames[booking.roomId] === 'Grande' ? 
                    `Grande salle (${booking.soloOrGroup === 'Group' ? 'en groupe' : 'en solo'})` : 'Petite salle'}</p>
                    <p><strong>Date :</strong> {formatDateTime(booking.startTime, booking.endTime)}</p>
                    
                    {isAdmin && userEmails[booking.userId] && (
                      <p><strong>Utilisateur :</strong> {userEmails[booking.userId].name} ({userEmails[booking.userId].email})</p>
                    )}

                    <p><strong>Status :</strong> {booking.status === 'pending' ? 'en cours' : 'confirmé'}</p>
                    
                    <p><strong>Code :</strong> {booking.reservationNumber}</p>
                    
                    {isAdmin && (
                      <button onClick={() => handleCancel(booking._id)} style={{ marginLeft: '10px', color: 'red' }}>
                        Annuler
                      </button>
                    )}
                  </li>
                ))
            ) : (
              <p>Aucune réservation passée.</p>
            )}
          </ul>
        </>
      )}
      <button onClick={handleRedirectToCalendar} style={{ fontSize: '18px' }}>
        Affichage du calendrier des salles
      </button>
    </div>
  );
};

export default Bookings;